import { formatDate, reverseFormatFormDate } from './dateTimeUtils';

/**
 * Check whether the given product is a gold product or not
 *
 * @param {string} productName - The product name
 * @returns {boolean} - False if the product name does not contain the keyword "gold"
 */
export const isGoldProduct = (productName) => {
  return productName
    .split(' ')
    .map((el) => {
      return el.toLowerCase();
    })
    .includes('gold');
};

/**
 * Map the rebate tier value to required display value to display on the screen.
 *
 *
 * @param {string} rebateTier - Family or single rebate tier value, e.g. "BASE_TIER", "TIER_1", "TIER_2", and "TIER_3".
 * @returns {string} - The mapped value to display on the page, e.g. "Base Tier", "Tier 1", "Tier 2", "Tier 3".
 */
export const summaryIncomeTier = (rebateTier) => {
  // if there is no rebate has been applied
  if (rebateTier === 'none') {
    return 'No Australian Government rebate is included in this premium.';
  }
  return rebateTier
    .split('_')
    .map((el) => {
      return el.charAt(0) + el.slice(1).toLowerCase();
    })
    .join(' ');
};

/*
 * Map the cover type data to valid param required by Needs Analysis Form refill
 *
 * @param {String} coverTypeData - The given cover type
 * @returns {string} - The required URL param data
 */
export const getCoverType = (coverTypeData) => {
  if (coverTypeData !== null) {
    switch (coverTypeData.toLowerCase()) {
      case 'hospital cover':
      case 'hospital only':
        return 'hospital';
      case 'extras cover':
      case 'extras only':
        return 'extras';
      default:
        return 'both';
    }
  }

  return null;
};

/**
 * Map the cover reason data to required data for needs analysis
 *
 * @param {string} coverReasonData - The array that contains the value of cover reason options
 * @returns {string} - The mapped value required for needs analysis data
 */
export const convertCoverReason = (coverReasonData) => {
  if (coverReasonData !== null) {
    switch (coverReasonData.toLowerCase()) {
      case (coverReasonData.toLowerCase().match(/specific/) || {}).input:
        return 'specific';
      case 'planning a family':
        return 'planningFamily';
      case 'total peace of mind':
        return 'peaceOfMind';
      case 'just the basics':
        return 'basics';
      default:
        return 'allRound';
    }
  }
  return null;
};

/**
 * Check whether the client has inputed children as dependants
 *
 * @param {Number} productName - The number of children
 * @returns {Number} - null if 0, else returned number of children
 */
export const hasChildren = (numberOfDependants) => {
  return numberOfDependants > 0 ? numberOfDependants : null;
};

/**
 * Returns URL required for returning to NeedsAnalysis
 *
 * @param {object} opportunity - Response opportunity data
 * @returns {string} - Needs Analysis URL with required parameters
 */
export const getNeedsAnalysisUrl = (opportunity, formDvaCard = null) => {
  const zeroTimeOffset = true;
  const newDvaCard = (opportunity.mainPolicyHolder?.veteranCard?.hasVeteranCard || opportunity.partner?.veteranCard?.hasVeteranCard) ?? opportunity.dvaCard;

  const needsAnalysisData = {
    /* Step 1 Data */
    DOB: reverseFormatFormDate(formatDate(opportunity.contact.dateOfBirth, zeroTimeOffset)),
    state: opportunity.contact.addressState.toUpperCase(),
    eligibility: opportunity.eligibility.eligibilityType ?? opportunity.eligibility,
    dvaCard: formDvaCard ?? newDvaCard,
    /* Step 2 Data */
    coverComposition: opportunity.composition,
    partnerDOB: reverseFormatFormDate(formatDate(opportunity.secondAdultDateofBirth, zeroTimeOffset)),
    numberOfChildren: hasChildren(parseInt(opportunity.numberOfDependants, 10)),
    familyPlus: RegExp(/(plus)/i).test(opportunity.composition), // `familyPlus` is calculated depend on the `coverComposition` and if it contains `plus` string (case insensitive)
    myCircumstances: opportunity.myCircumstances,
    incomeTier: opportunity.rebateTier,
    /* Step 3 Data */
    coverType: getCoverType(opportunity.coverType),
    coverReason: convertCoverReason(opportunity.need),
    hospitalNeeds: opportunity.hospitalNeeds ? opportunity.hospitalNeeds.join(';').replace(/,/g, '') : '',
    extrasNeeds: opportunity.extrasNeeds ? opportunity.extrasNeeds.join(';') : '',
    noPregnancy: opportunity.pregnancy !== null ? !opportunity.pregnancy : '', // `pregnancy` field means "I want pregnancy", therefore it should be "flipped" when going back to the Get A Quote form.
  };
  
  return `&${Object.keys(needsAnalysisData)
    .map((key) => {
      return [key, needsAnalysisData[key]].join('=');
    })
    .join('&')}&`;
};
